'use client';

import { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  FileText,
  Receipt,
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { formatCurrency } from '../../../types';
import {
  journalEntries,
  paymentEntries,
  bankTransactions,
  getAccountsStats,
} from '../data/mock-data';
import {
  journalEntryStatusConfig,
  paymentTypeConfig,
  paymentStatusConfig,
} from '../types';
import type { AccountsPage } from '../index';

type TabType = 'journal' | 'payments';

interface AccountsDashboardProps {
  onNavigate: (page: AccountsPage) => void;
}

export function AccountsDashboard({ onNavigate }: AccountsDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('journal');
  const stats = getAccountsStats();

  // 최근 분개 (5개)
  const recentJournalEntries = journalEntries.slice(0, 5);

  // 최근 결제 (5개)
  const recentPayments = paymentEntries.slice(0, 5);

  // 최근 은행 거래 (미조정 건)
  const unreconciledTransactions = bankTransactions.filter((t) => !t.isReconciled);

  const journalColumns = [
    {
      key: 'code',
      label: '전표번호',
      width: '25%',
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'entryDate',
      label: '일자',
      width: '20%',
    },
    {
      key: 'entryType',
      label: '유형',
      width: '15%',
    },
    {
      key: 'totalDebit',
      label: '금액',
      width: '25%',
      align: 'right' as const,
      render: (value: unknown) => formatCurrency(value as number),
    },
    {
      key: 'status',
      label: '상태',
      width: '15%',
      render: (value: unknown) => {
        const status = value as keyof typeof journalEntryStatusConfig;
        const config = journalEntryStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
  ];

  const paymentColumns = [
    {
      key: 'code',
      label: '결제번호',
      width: '25%',
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'paymentType',
      label: '유형',
      width: '20%',
      render: (value: unknown) => {
        const type = value as keyof typeof paymentTypeConfig;
        const config = paymentTypeConfig[type];
        const colorClasses: Record<string, string> = {
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'partyName',
      label: '거래처',
      width: '30%',
      render: (value: unknown) => value ? String(value) : '-',
    },
    {
      key: 'amount',
      label: '금액',
      width: '25%',
      align: 'right' as const,
      render: (value: unknown) => formatCurrency(value as number),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">현금 및 예금</p>
              <p className="text-xl font-bold mt-1 truncate">
                {formatCurrency(stats.bankBalance)}
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">매출채권</p>
              <p className="text-xl font-bold mt-1 truncate">
                {formatCurrency(stats.totalReceivable)}
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">매입채무</p>
              <p className="text-xl font-bold mt-1 text-red-500 truncate">
                {formatCurrency(stats.totalPayable)}
              </p>
            </div>
            <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <ArrowDownRight className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground truncate">당기순이익</p>
              <p className={`text-xl font-bold mt-1 truncate ${stats.netIncome >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatCurrency(stats.netIncome)}
              </p>
            </div>
            <div className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center ${
              stats.netIncome >= 0
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
              {stats.netIncome >= 0 ? (
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 재무 요약 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <PiggyBank className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-sm">자산</h3>
          </div>
          <div className="text-xl font-bold">{formatCurrency(stats.totalAssets)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            유동자산 + 고정자산
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <CreditCard className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="font-semibold text-sm">부채</h3>
          </div>
          <div className="text-xl font-bold">{formatCurrency(stats.totalLiabilities)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            유동부채 + 장기부채
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Wallet className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold text-sm">자본</h3>
          </div>
          <div className="text-xl font-bold">{formatCurrency(stats.totalEquity)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            자본금 + 이익잉여금
          </div>
        </div>
      </div>

      {/* 탭 형태 테이블 섹션 */}
      <div className="bg-card border border-border rounded-lg">
        {/* 탭 헤더 */}
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex">
            <button
              onClick={() => setActiveTab('journal')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'journal'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <FileText className="w-4 h-4" />
              최근 분개
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'journal'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{recentJournalEntries.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'payments'
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Receipt className="w-4 h-4" />
              최근 결제
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded ${
                activeTab === 'payments'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>{recentPayments.length}</span>
            </button>
          </div>
          <button
            onClick={() => onNavigate(activeTab === 'journal' ? 'journal' : 'payments')}
            className="text-sm text-primary hover:underline flex items-center gap-1 pr-4"
          >
            전체보기
          </button>
        </div>

        {/* 탭 컨텐츠 */}
        <div className="p-4">
          {activeTab === 'journal' ? (
            <DataTable
              data={recentJournalEntries}
              columns={journalColumns}
              pageSize={5}
              showPagination={false}
              emptyMessage="분개 내역이 없습니다."
            />
          ) : (
            <DataTable
              data={recentPayments}
              columns={paymentColumns}
              pageSize={5}
              showPagination={false}
              emptyMessage="결제 내역이 없습니다."
            />
          )}
        </div>
      </div>

      {/* 미조정 은행 거래 */}
      {unreconciledTransactions.length > 0 && (
        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-yellow-700 dark:text-yellow-400">
                미조정 은행 거래
              </h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400">
                {unreconciledTransactions.length}건
              </span>
            </div>
            <button
              onClick={() => onNavigate('reconciliation')}
              className="text-sm text-primary hover:underline"
            >
              조정하기
            </button>
          </div>
          <div className="space-y-2">
            {unreconciledTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-card"
              >
                <div>
                  <div className="font-medium">{transaction.description}</div>
                  <div className="text-xs text-muted-foreground">
                    {transaction.transactionDate} | {transaction.bankAccountName}
                  </div>
                </div>
                <div className={`font-bold ${
                  transaction.transactionType === 'deposit'
                    ? 'text-green-500'
                    : 'text-red-500'
                }`}>
                  {transaction.transactionType === 'deposit' ? '+' : '-'}
                  {formatCurrency(transaction.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 빠른 작업 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">빠른 작업</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate('journal')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <FileText className="w-6 h-6 text-blue-500" />
            <span className="text-sm font-medium">분개 입력</span>
          </button>
          <button
            onClick={() => onNavigate('payments')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <Receipt className="w-6 h-6 text-green-500" />
            <span className="text-sm font-medium">결제 등록</span>
          </button>
          <button
            onClick={() => onNavigate('balance-sheet')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <PiggyBank className="w-6 h-6 text-purple-500" />
            <span className="text-sm font-medium">대차대조표</span>
          </button>
          <button
            onClick={() => onNavigate('income-statement')}
            className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            <TrendingUp className="w-6 h-6 text-orange-500" />
            <span className="text-sm font-medium">손익계산서</span>
          </button>
        </div>
      </div>
    </div>
  );
}
